import { filterBy } from "@progress/kendo-data-query";
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
  DropDownListHandle,
  DropDownListProps,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import { AxiosError } from "axios";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import useDeepCompareEffect from "use-deep-compare-effect";
import { TlxHttpResponse } from "@/api";
import Errors from "@/components/Dialog/Error";
import FormLabel from "./FormLabel";
import FormSelectItemRender from "./FormSelectItemRender";
import FormSelectValueRender from "./FormSelectValueRender";
import IconHint from "./IconHint";
import ListNoData from "./ListNoData";
import { useControlledField, type FieldValidator } from "./useControlledField";

export interface SelectData {
  checked?: boolean;
  name: string | undefined;
  id?: string | number | string[] | undefined;
  disabled?: boolean;
  checkAllDisabled?: boolean;
  custom?: string;
  groupField?: string;
  title?: string;
}

type ExtraDropDownProps = Omit<
  DropDownListProps,
  "name" | "value" | "onChange" | "valid" | "data" | "ref"
> & {
  placeholder?: string;
  allowCustom?: boolean;
};

export interface FormDropDownListProps extends ExtraDropDownProps {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  data?: SelectData[];
  noDataMessage?: string;
  filterable?: boolean;
  popupWidth?: string;
  apiError?: AxiosError<TlxHttpResponse>;
  filterByProperty?: string;
  imageRender?: (dataItem: ListItemProps["dataItem"]) => React.ReactNode;
  descriptionField?: string;
  disableAutoClear?: boolean;
  fieldWrapperClassName?: string;
  /** When false, suppresses validation error display regardless of touched state */
  showError?: boolean;
  required?: boolean;
  "data-test-id"?: string;
  /**
   * Optional side-effect callback after this field's RHF value is updated by a
   * **user** picking from the dropdown.
   *
   * **When to use**
   * - Cascade updates to *other* form fields when the user makes a selection
   *   (e.g. picking an attribute column auto-fills `displayName`, `type`).
   * - Port legacy Kendo `<Field component={FormDropDownList} onChange={...}>`
   *   handlers into RHF slide-panel forms.
   *
   * **When to avoid**
   * - Do not mirror this field's value via `useEffect` + `useWatch` and then call
   *   `formApi.onChange` — that pattern can cause infinite update loops.
   * - Do not use for API-driven or edit-mode initial hydration; use
   *   `formApi.setProgrammaticValue` / `setProgrammaticValues` instead.
   * - Do not write *this* field's value here; RHF is already updated internally.
   *
   * **Not called** on mount, `defaultValue` seeding, data auto-clear, or any
   * programmatic `setValue` / `reset`.
   */
  onSelectionChange?: (value: unknown) => void;
  /**
   * Keep a saved value that is no longer in `data` (archived, restricted):
   * it stays selected and listed, disabled, instead of being auto-cleared.
   * For fields that reference a saved record which may since have changed.
   */
  retainCurrentValue?: boolean;
}

export const FormDropDownList: React.FC<FormDropDownListProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional = false,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  data,
  noDataMessage,
  filterable = true,
  popupWidth,
  apiError,
  filterByProperty,
  imageRender,
  descriptionField,
  disableAutoClear,
  fieldWrapperClassName,
  textField = "name",
  dataItemKey = "id",
  itemRender,
  valueRender,
  defaultValue,
  defaultItem,
  loading,
  allowCustom,
  onSelectionChange,
  retainCurrentValue = false,
  "data-test-id": testId,
  ...others
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const methods = useFormContext();
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showApiError = !loading && apiError;
  const showHint = !!hint;
  const inputId = name;
  const labelId = `${inputId}_label`;
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";
  const current = field.value as SelectData | string | null | undefined;
  const idAttr = dataItemKey as keyof SelectData;
  const dataSource = useMemo(() => {
    const list = data ?? [];
    if (!retainCurrentValue || !current) return list;
    // A value may be a bare id rather than an option object; retain it as a
    // disabled option labelled by that id rather than spreading the string.
    const retained: SelectData =
      typeof current === "object"
        ? { ...current, disabled: true }
        : {
            name: current,
            [idAttr]: current,
            [textField]: current,
            disabled: true,
          };
    return list.some(item => item[idAttr] === retained[idAttr])
      ? list
      : [...list, retained];
  }, [data, retainCurrentValue, current, idAttr, textField]);
  const [dropDownData, setDropDownData] = useState<SelectData[]>(dataSource);
  const dropDownRef = useRef<DropDownListHandle>(null);

  useDeepCompareEffect(() => {
    setDropDownData(dataSource);
  }, [dataSource]);

  // Default-value seeding on first render only — programmatic, must NOT dirty.
  const seededDefault = useRef(false);
  useEffect(() => {
    if (!seededDefault.current && defaultValue && field.value == null) {
      methods.setValue(name, defaultValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      seededDefault.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-clear stale selection when data refreshes and the current value is no
  // longer in the list. Programmatic — must NOT dirty the field.
  useDeepCompareEffect(() => {
    if (
      !disableAutoClear &&
      !retainCurrentValue &&
      dataSource.length > 0 &&
      !loading &&
      field.value &&
      !allowCustom
    ) {
      const stillPresent = dataSource.find(
        item =>
          item[idAttr] === (current as SelectData | null | undefined)?.[idAttr],
      );
      if (!stillPresent) {
        methods.setValue(name, null, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }
  }, [
    dataSource,
    field.value,
    loading,
    disableAutoClear,
    retainCurrentValue,
    dataItemKey,
    allowCustom,
  ]);

  const onInputChange = (event: DropDownListChangeEvent) => {
    // Picking the "Choose…" default item means "no value", not that object.
    // Match by key only when the default item has one, so an option that also
    // lacks it isn't mistaken for the default.
    const isDefaultItem =
      !!defaultItem &&
      (event.value === defaultItem ||
        (defaultItem[dataItemKey] !== undefined &&
          event.value?.[dataItemKey] === defaultItem[dataItemKey]));
    const value = isDefaultItem ? null : event.value;
    field.onChange(value);
    onSelectionChange?.(value);
  };

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    if (filterByProperty) {
      setDropDownData(
        filterBy([...dataSource], {
          logic: "or",
          filters: [event.filter, { ...event.filter, field: filterByProperty }],
        }),
      );
      return;
    }
    setDropDownData(filterBy([...dataSource], event.filter));
  };

  const content = (
    <FieldWrapper className={fieldWrapperClassName}>
      <FormLabel
        id={labelId}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        label={label}
        value={
          (field.value as SelectData | null)?.[
            textField as keyof SelectData
          ] as string | undefined
        }
        floatLabel={floatLabel}
        // A shown default item occupies the box, so the label must stay raised.
        placeholder={placeholder ?? defaultItem?.[textField] ?? undefined}
        optional={optional}
      >
        <DropDownList
          {...others}
          defaultItem={defaultItem}
          name={name}
          ariaDescribedBy={`${hintId} ${errorId}`}
          aria-invalid={!!fieldState.error || undefined}
          ref={dropDownRef}
          ariaLabelledBy={labelId}
          textField={textField}
          dataItemKey={dataItemKey}
          valid={!fieldState.error}
          value={(field.value as SelectData | null | undefined) ?? null}
          disabled={disabled}
          filterable={filterable}
          style={{ width: "100%" }}
          data={dropDownData}
          onFilterChange={filterChange}
          onChange={onInputChange}
          onBlur={field.onBlur}
          skipDisabledItems={true}
          loading={loading}
          {...(allowCustom !== undefined ? { allowCustom } : {})}
          valueRender={
            valueRender
              ? valueRender
              : (element, value) =>
                  FormSelectValueRender(element, textField, value, imageRender)
          }
          itemRender={
            itemRender
              ? itemRender
              : (li, itemProps) =>
                  FormSelectItemRender(li, {
                    ...itemProps,
                    descriptionField,
                    imageRender,
                  })
          }
          id={`${inputId}_input`}
          popupSettings={
            popupWidth
              ? { width: popupWidth, animate: true }
              : { animate: true }
          }
          listNoDataRender={() => (
            <ListNoData message={noDataMessage} loading={loading} />
          )}
        />
        {showHint && !hintIcon && <Hint id={hintId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} />}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
        {showApiError && <Errors errors={[apiError]} />}
      </FormLabel>
    </FieldWrapper>
  );
  // Kendo's DropDownList drops unknown props, so the test hook lives on a
  // wrapper this component owns.
  return testId ? <div data-test-id={testId}>{content}</div> : content;
};

export default FormDropDownList;
